import { useCallback, useEffect, useState } from 'react';
import { TerminalChildrenModeSettings } from '../../types/Admin/TerminalAppearanceTypes';

type UseChildrenModeControllerParams = {
  childrenModeSettings?: TerminalChildrenModeSettings | null;
  currentIndex: number;
  activeModal: boolean;
  isLoadingGetTicket: boolean;
};

export const useChildrenModeController = ({
  childrenModeSettings,
  currentIndex,
  activeModal,
  isLoadingGetTicket,
}: UseChildrenModeControllerParams) => {
  const activateChildrenMode = childrenModeSettings?.activateChildrenMode ?? false;
  const showToggleButtonSetting = childrenModeSettings?.showToggleButton ?? false;
  const switchByAgeEnabledSetting = childrenModeSettings?.switchByAgeEnabled ?? false;
  const ageThreshold = childrenModeSettings?.ageThreshold ?? 14;

  const [sessionChildrenMode, setSessionChildrenMode] = useState(false);
  // Принудительный режим должен переключаться только когда терминал свободен,
  // иначе визуально “мигает” в середине сессии посетителя.
  const [effectiveForced, setEffectiveForced] = useState(activateChildrenMode);

  const isVisitorSessionActive =
    currentIndex > 0 || activeModal || isLoadingGetTicket;

  useEffect(() => {
    // Если терминал занят — сохраняем текущее принудительное состояние,
    // чтобы переключение не происходило “мгновенно” в середине сессии.
    if (isVisitorSessionActive) return;
    setEffectiveForced(activateChildrenMode);
  }, [activateChildrenMode, isVisitorSessionActive]);

  const isChildrenMode = effectiveForced || sessionChildrenMode;
  const endSessionChildrenMode = useCallback(() => {
    if (!effectiveForced) {
      setSessionChildrenMode(false);
    }
  }, [effectiveForced]);

  const toggleSessionChildrenMode = useCallback(() => {
    if (effectiveForced) {
      return;
    }
    setSessionChildrenMode((prev) => !prev);
  }, [effectiveForced]);

  const enableSessionChildrenMode = useCallback(() => {
    if (effectiveForced) {
      return;
    }
    setSessionChildrenMode(true);
  }, [effectiveForced]);

  const showChildrenModeButton =
    showToggleButtonSetting &&
    !effectiveForced &&
    !activeModal &&
    (currentIndex === 0 || sessionChildrenMode);
  return {
    isChildrenMode,
    effectiveForced,
    sessionChildrenMode,
    showChildrenModeButton,
    toggleSessionChildrenMode,
    enableSessionChildrenMode,
    endSessionChildrenMode,
    switchByAgeEnabled: switchByAgeEnabledSetting && !effectiveForced,
    ageThreshold,
  };
};
